import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { assert_array_typeof_string, assert_instanceof } from '@shared/asserts/utils_asserts';
import { CONSTANTS__shared } from '@shared/constants/CONSTANTS__shared';
import { ENUM__string_id_empty } from '@shared/constants/ENUM__string_id_empty';
import { INVOCATION__base } from '@shared/dto/generation/graph_invoke/_invocations/_base/INVOCATION__base';
import { memo, useMemo } from 'react';
import { G_VIEW__graph_field_errors } from './fields/G_VIEW__graph_field_errors';
import { G_GRAPH_NODE__invocation_fields } from './G_GRAPH_NODE__invocation_fields';
import { G_GRAPH_NODE__invocation_overlay_not_used } from './G_GRAPH_NODE__invocation_overlay_not_used';
import { G_LABEL__hash } from './G_LABEL__hash';
import { G_BUTTON__node_remove } from './title/G_BUTTON__node_remove';
import { G_BUTTON__node_show_hidden_fields } from './title/G_BUTTON__node_show_hidden_fields';

interface PROPS {
  data: INVOCATION__base;
}

export const G_GRAPH_NODE__invocation = memo(function G_GRAPH__node_invocation({ data }: PROPS) {
  let node = data;

  assert_instanceof(node, INVOCATION__base);
  const node_id = node.meta.node_id;

  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { g_graph__node_id_selected, g_graph__node_id_hovered, g_graph__new_edge_field } = use_slice_ui([
    'g_graph__node_id_selected',
    'g_graph__node_id_hovered',
    'g_graph__new_edge_field',
  ]);

  /*
   * STATE
   */

  let { graph } = generation_current.tool;

  const opacity = useMemo(() => get_node_opacity(), [node.meta.node_id, g_graph__node_id_selected, graph, g_graph__node_id_hovered, g_graph__new_edge_field]);

  const is_selected = useMemo(() => g_graph__node_id_selected === node_id, [node_id, g_graph__node_id_selected]);

  /*
   * METHODS
   */
  function get_node_opacity() {
    let out = 0;

    if (g_graph__node_id_selected == ENUM__string_id_empty.UUID) return 100;
    if (g_graph__new_edge_field.meta.node_id == node_id) return 100;
    if (g_graph__node_id_selected === node_id) return 100;
    if (g_graph__node_id_hovered == node_id) return 100;

    let graph_dagre = graph.dagre__convert_to();

    let connected_ids_level_1 = graph.nodes_map.size() > 1 ? graph_dagre.neighbors(g_graph__node_id_selected) : [];
    connected_ids_level_1 ||= [];

    let connected_ids_all = graph.dagre__get_connected_ids(graph_dagre, g_graph__node_id_selected);

    assert_array_typeof_string(connected_ids_level_1);

    if (connected_ids_level_1.includes(node_id)) {
      out = 90;
    } else if (connected_ids_all.includes(node_id)) {
      out = 60;
    } else {
      out = 30;
    }

    return out;
  }

  /*
   * RENDERS
   */

  function render_title_text() {
    let text = node.meta.node_role.is_blank() ? `type: ${node.type}` : node.meta.node_role;

    return <span>{text}</span>;
  }

  /*
   * HTML
   */

  let [width, height] = [`${node.meta.graph_width_px}px`, `${node.meta.graph_height_px}px`];

  let [minWidth, maxWidth] = [width, width];
  let [minHeight, maxHeight] = [height, height];

  if (node.meta.graph_id != graph.meta.graph_id) return null;

  let node_exists = graph.nodes_map.has(node.meta.node_id);
  if (!node_exists) return <></>; //node deleted

  return (
    <div
      className={`relative rounded-lg   bg-component text-white  transition-opacity
    ${is_selected ? 'shadow-[0_0_0_2px_theme(colors.blue.500)]' : 'shadow-[0px_0px_0px_1px_theme(colors.gray.600)]'}
        `}
      style={{
        width,
        minWidth,
        maxWidth,
        height,
        minHeight,
        maxHeight,

        opacity: opacity / 100,
      }}
    >
      {/* TITLE */}
      <div
        className={`flex flex-row items-center justify-between gap-2 p-3 
          rounded-t-lg bg-bar
          
          shadow-[0_1px_0_0_theme(colors.gray.600)]
          text-xs truncate font-semibold text-left`}
        style={{ height: `${CONSTANTS__shared.GRID_CELL_SIZE * 2}px` }}
      >
        {!node.meta.errors.is_empty() && <G_VIEW__graph_field_errors errors={node.meta.errors} />}
        <G_LABEL__hash hash={node.meta.node_hash} has_effect_on_generation={true} />

        {render_title_text()}

        <G_BUTTON__node_remove invocation={node} />
        <G_BUTTON__node_show_hidden_fields invocation={node} />
      </div>

      {/* FIELDS */}
      <div className="">
        <G_GRAPH_NODE__invocation_fields title="Inputs" fields_map={node.fields__get_visible_in_graph_node_inputs()} node={node} />
        <G_GRAPH_NODE__invocation_fields title="Outputs" fields_map={node.fields__get_visible_in_graph_node_outputs()} node={node} />
      </div>
      <div
        className={`flex flex-row items-center justify-between gap-2 pl-2
                    border-t border-gray-600 
                    text-xs truncate font-semibold text-left`}
        style={{ height: `${CONSTANTS__shared.GRID_CELL_SIZE}px` }}
      >
        {node.meta.graph_x_px}:{node.meta.graph_y_px}
      </div>

      <G_GRAPH_NODE__invocation_overlay_not_used invocation={node} />
    </div>
  );
});
